import React, { useEffect, useState, useRef } from 'react';
import Button from './Button';
import { useSounds } from '../../hooks';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  isOpen,
  onClose,
  userName = 'Friend',
}) => {
  const [showContent, setShowContent] = useState(false);
  const { playSound } = useSounds();
  const hasPlayedSoundRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      // Play welcome sound when modal opens
      if (!hasPlayedSoundRef.current) {
        hasPlayedSoundRef.current = true;
        // Delay sound slightly to sync with animation
        setTimeout(() => playSound('welcome'), 200);
      }

      // Delay content appearance for whale animation
      const timer = setTimeout(() => setShowContent(true), 300);
      document.body.style.overflow = 'hidden';
      return () => {
        clearTimeout(timer);
        document.body.style.overflow = 'unset';
      };
    } else {
      setShowContent(false);
      hasPlayedSoundRef.current = false;
      document.body.style.overflow = 'unset';
    }
  }, [isOpen, playSound]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-400 to-indigo-600" />

      {/* Bubbles decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/20"
            style={{
              width: Math.random() * 40 + 20,
              height: Math.random() * 40 + 20,
              left: `${Math.random() * 100}%`,
              bottom: `${Math.random() * 50}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center text-center px-4">
        {/* Jumping Whale */}
        <div className="mb-8 animate-jump">
          <span className="text-[120px] drop-shadow-lg">{'\u{1F433}'}</span>
        </div>

        {/* Welcome Text */}
        {showContent && (
          <div className="animate-pop flex flex-col items-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
              Welcome, {userName}!
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-md">
              Ready to learn and have fun? Let's practice together!
            </p>

            <Button
              onClick={onClose}
              variant="success"
              size="large"
            >
              Let's Go! {'\u{1F680}'}
            </Button>
          </div>
        )}
      </div>

      {/* CSS for floating bubbles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-100px) rotate(180deg); }
        }
      `}</style>
    </div>
  );
};

export default WelcomeModal;
